import {AfterViewInit, Component, OnDestroy, ViewChild} from '@angular/core';
import {
  AlertsByShiftIdsGQL,
  JustificationStatus,
  WatchesFiltersGQL,
  WatchesFiltersQuery,
  WatchStatus,
} from '../../../generated/graphql';
import {Pagination} from '../../utils/Pagination';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {WatchesService} from '../services/watches.service';
import {Table} from 'primeng/table';
import {RejectCallDialogComponent, RejectCallDialogResult} from '../../alerts/calls/reject-call-dialog/reject-call-dialog.component';
import {LazyLoadEvent} from 'primeng/api';
import {CallsService} from '../../alerts/calls/calls.service';

export interface ShiftRow {
  id: number;
  lawyerName: string;
  date: Date;
  judicialDistrict: string;
  matter: string;
  justificationStatus: JustificationStatus;
  rejectionReason: string | null;
  // Keep reference to mutate status in-place
  _watchRef: { justificationStatus: JustificationStatus; rejectionReason?: string | null };
}

export interface AlertSubRow {
  alertId: number;
  shiftId: number;
  alertType: string;
  date: Date;
  justificationStatus: JustificationStatus;
  rejectionReason: string | null;
}

@Component({
  selector: 'app-justify-shifts-table',
  templateUrl: './justify-shifts-table.component.html',
  styleUrls: ['./justify-shifts-table.component.scss'],
})
export class JustifyShiftsTableComponent implements AfterViewInit, OnDestroy {
  @ViewChild('table')
  table: Table;

  pagination: Pagination;
  filters: WatchesFiltersQuery;

  rows: ShiftRow[] = [];
  allRows: ShiftRow[] = [];
  totalElements: number = 0;

  alertsByShift: Map<number, AlertSubRow[]> = new Map();
  expandedShiftId: number | null = null;

  justificationStatusOptions = [
    { label: 'Pendiente', value: JustificationStatus.Pending },
    { label: 'Aceptado', value: JustificationStatus.Accepted },
    { label: 'Rechazado', value: JustificationStatus.Rejected },
  ];

  private activeJustificationFilter: JustificationStatus[] | null = null;
  private activeSortField: string | null = null;
  private activeSortOrder: number = 1;

  private readonly justificationStatusOrder: Record<JustificationStatus, number> = {
    [JustificationStatus.Pending]: 0,
    [JustificationStatus.Accepted]: 1,
    [JustificationStatus.Rejected]: 2,
  };

  private ready = false;

  constructor(
    getFilters: WatchesFiltersGQL,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private watchesService: WatchesService,
    private alertsByShiftIdsGQL: AlertsByShiftIdsGQL,
    private callsService: CallsService,
  ) {
    getFilters.fetch().subscribe(s => (this.filters = s.data));
  }

  ngAfterViewInit(): void {
    this.table.filters['status'] = [
      { value: [WatchStatus.Ok], matchMode: 'contains', operator: 'and' },
    ];
    this.ready = true;
    this.onLazyLoad(this.table.createLazyLoadMetadata());
  }

  ngOnDestroy(): void {}

  onLazyLoad(event: LazyLoadEvent): void {
    if (event.sortField === 'justificationStatus') {
      this.activeSortField = 'justificationStatus';
      this.activeSortOrder = event.sortOrder ?? 1;
      this.applyJustificationFilter();
      return;
    }
    this.activeSortField = event.sortField ?? null;
    this.activeSortOrder = event.sortOrder ?? 1;
    this.pagination = new Pagination(event);
    this.updateElements();
  }

  updateElements(): void {
    if (this.ready)
      this.watchesService
        .list(this.pagination)
        .subscribe(s => {
          this.totalElements = (s.data.watches as any).totalElements ?? 0;
          this.allRows = this.buildRows(s.data.watches.content as any[]);
          this.applyJustificationFilter();
          this.loadAlertsByShifts(this.allRows.map(r => r.id));
        });
  }

  private loadAlertsByShifts(shiftIds: number[]): void {
    if (shiftIds.length === 0) {
      this.alertsByShift = new Map();
      return;
    }
    this.alertsByShiftIdsGQL.fetch({ shiftIds }).subscribe(result => {
      const map = new Map<number, AlertSubRow[]>();
      for (const alert of result.data.alertsByShiftIds) {
        if (!alert.allEvents) continue;
        for (const event of alert.allEvents) {
          if (!event || event.__typename !== 'Call') continue;
          const call = event as Extract<typeof event, { __typename: 'Call' }>;
          if (!call.watch) continue;
          const shiftId = call.watch.id;
          if (!map.has(shiftId)) {
            map.set(shiftId, []);
          }
          const alertType = alert.alertType?.name ?? alert.alias?.name ?? '';
          map.get(shiftId)!.push({
            alertId: call.id,
            shiftId,
            alertType,
            date: alert.date,
            justificationStatus: call.justificationStatus,
            rejectionReason: call.rejectionReason ?? null,
          });
        }
      }
      this.alertsByShift = map;
    });
  }

  toggleExpand(shiftId: number): void {
    this.expandedShiftId = this.expandedShiftId === shiftId ? null : shiftId;
  }

  getShiftAlerts(shiftId: number): AlertSubRow[] {
    return this.alertsByShift.get(shiftId) ?? [];
  }

  onJustificationStatusFilter(values: JustificationStatus[]): void {
    this.activeJustificationFilter = values && values.length > 0 ? values : null;
    this.applyJustificationFilter();
  }

  private applyJustificationFilter(): void {
    let result: ShiftRow[];
    if (!this.activeJustificationFilter || this.activeJustificationFilter.length === 0) {
      result = this.allRows;
    } else {
      result = this.allRows.filter(r =>
        this.activeJustificationFilter!.includes(r.justificationStatus)
      );
    }

    if (this.activeSortField === 'justificationStatus') {
      const order = this.justificationStatusOrder;
      const dir = this.activeSortOrder;
      result = [...result].sort((a, b) =>
        dir * (order[a.justificationStatus] - order[b.justificationStatus])
      );
    }

    this.rows = result;
  }

  private buildRows(watches: any[]): ShiftRow[] {
    return watches.map(watch => ({
      id: watch.id,
      lawyerName: watch.lawyer?.fullName ?? '',
      date: watch.start,
      judicialDistrict: watch.judicialDistrict?.name ?? '',
      matter: watch.alias?.name ?? watch.alertType?.name ?? '',
      justificationStatus: watch.justificationStatus as JustificationStatus,
      rejectionReason: watch.rejectionReason ?? null,
      _watchRef: watch,
    }));
  }

  approveShift(row: ShiftRow): void {
    this.watchesService.approveShift(row.id).subscribe(result => {
      const updated = result?.data?.watch;
      if (updated) {
        row.justificationStatus = updated.justificationStatus as JustificationStatus;
        row._watchRef.justificationStatus = updated.justificationStatus as JustificationStatus;
        this.applyJustificationFilter();
      }
    }, () => {
      this.snackBar.open('Error al aprobar la guardia', 'Cerrar', {
        verticalPosition: 'top',
        horizontalPosition: 'right',
      });
    });
  }

  rejectShift(row: ShiftRow): void {
    this.dialog
      .open(RejectCallDialogComponent, { width: '400px' })
      .afterClosed()
      .subscribe((result: RejectCallDialogResult | undefined) => {
        if (!result) return;
        this.watchesService.rejectShift(row.id, result.reason).subscribe(response => {
          const updated = response?.data?.watch;
          if (updated) {
            row.justificationStatus = updated.justificationStatus as JustificationStatus;
            row.rejectionReason = updated.rejectionReason ?? null;
            row._watchRef.justificationStatus = updated.justificationStatus as JustificationStatus;
            row._watchRef.rejectionReason = updated.rejectionReason ?? null;
            this.applyJustificationFilter();
          }
        }, () => {
          this.snackBar.open('Error al rechazar la guardia', 'Cerrar', {
            verticalPosition: 'top',
            horizontalPosition: 'right',
          });
        });
      });
  }

  justificationStatusIcon(status: JustificationStatus): string {
    switch (status) {
      case JustificationStatus.Accepted:
        return 'check-circle-outline';
      case JustificationStatus.Rejected:
        return 'close-circle-outline';
      case JustificationStatus.Pending:
      default:
        return 'clock-outline';
    }
  }

  justificationStatusColor(status: JustificationStatus): string {
    switch (status) {
      case JustificationStatus.Accepted:
        return 'green-text';
      case JustificationStatus.Rejected:
        return 'red-text';
      case JustificationStatus.Pending:
      default:
        return 'grey-text';
    }
  }

  getJustificationTooltip(status: JustificationStatus, reason: string | null): string {
    if (status === JustificationStatus.Rejected) {
      return reason ? reason : 'Sin motivo';
    }
    return status;
  }

  approveAlertCall(alertRow: AlertSubRow): void {
    this.callsService.approveCall(alertRow.alertId).subscribe(result => {
      const updated = result?.data?.call;
      if (updated) {
        alertRow.justificationStatus = updated.justificationStatus;
      }
    }, () => {
      this.snackBar.open('Error al aprobar el aviso', 'Cerrar', {
        verticalPosition: 'top',
        horizontalPosition: 'right',
      });
    });
  }

  rejectAlertCall(alertRow: AlertSubRow): void {
    this.dialog
      .open(RejectCallDialogComponent, { width: '400px' })
      .afterClosed()
      .subscribe((result: RejectCallDialogResult | undefined) => {
        if (!result) return;
        this.callsService.rejectCall(alertRow.alertId, result.reason).subscribe(response => {
          const updated = response?.data?.call;
          if (updated) {
            alertRow.justificationStatus = updated.justificationStatus;
            alertRow.rejectionReason = updated.rejectionReason ?? null;
          }
        }, () => {
          this.snackBar.open('Error al rechazar el aviso', 'Cerrar', {
            verticalPosition: 'top',
            horizontalPosition: 'right',
          });
        });
      });
  }
}
